import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CheckCircle, Clock } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regBio, setRegBio] = useState("");
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;
  if (user === null) {
    return (
      <div className="flex items-center justify-center h-screen mesh-bg">
        <Loader2 className="w-6 h-6 text-[#F7941D] animate-spin" />
      </div>
    );
  }

  if (applicationSubmitted) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4" data-testid="application-submitted">
        <div className="w-full max-w-md text-center animate-fade-in-up">
          <div className="flex items-center justify-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-full bg-[#F7941D] flex items-center justify-center glow-orange">
              <span className="text-white text-sm font-bold">E</span>
            </div>
            <span className="text-2xl font-bold text-white/90 tracking-tight">Eamico</span>
          </div>
          <div className="w-16 h-16 rounded-full bg-[#F7941D]/10 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[#F7941D]" />
          </div>
          <h1 className="text-2xl font-semibold text-white/90 mb-3">Application Submitted!</h1>
          <p className="text-white/45 text-sm leading-relaxed mb-6">
            Thank you for applying to the Eamico Ambassador Program. Our team will review your application and you'll receive an email once you've been approved.
          </p>
          <div className="glass rounded-2xl p-4 text-left">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-[#F7941D] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-white/80">What happens next?</p>
                <p className="text-xs text-white/40 mt-1">An admin will review your application. Once approved, you'll get an email with your Staff ID, referral code, and unique referral link.</p>
              </div>
            </div>
          </div>
          <button onClick={() => { setApplicationSubmitted(false); setTab("login"); }} className="mt-6 text-sm text-[#F7941D] hover:text-[#ff9f2e] font-medium transition-colors" data-testid="back-to-login">
            Back to Sign In
          </button>
          <p className="text-xs text-white/20 mt-8">Eamico Inc. &middot; Write, ship, and scale.</p>
        </div>
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(loginEmail, loginPass);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email: regEmail, password: regPass, name: regName, bio: regBio });
      if (data.message === "application_submitted") {
        setApplicationSubmitted(true);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-bg-alt flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-full bg-[#F7941D] flex items-center justify-center glow-orange">
            <span className="text-white text-sm font-bold">E</span>
          </div>
          <span className="text-2xl font-bold text-white/90 tracking-tight">Eamico</span>
        </div>

        <div className="glass-strong rounded-2xl p-6" data-testid="auth-card">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white/90">Ambassador Portal</h2>
            <p className="text-white/40 text-sm mt-1">Sign in or apply to become a brand ambassador</p>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="w-full bg-white/[0.04] mb-6 rounded-xl border border-white/[0.06]">
              <TabsTrigger value="login" className="flex-1 rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" className="flex-1 rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="register-tab">Apply</TabsTrigger>
            </TabsList>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="auth-error">
                {error}
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-white/60 text-sm font-medium">Email</Label>
                  <Input id="login-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required placeholder="you@example.com" className="glass-input rounded-xl h-11 text-sm" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-white/60 text-sm font-medium">Password</Label>
                  <Input id="login-password" type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} required placeholder="Enter password" className="glass-input rounded-xl h-11 text-sm" data-testid="login-password-input" />
                </div>
                <button type="submit" className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(247,148,29,0.3)] disabled:opacity-50" disabled={loading} data-testid="login-submit-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="text-white/60 text-sm font-medium">Full Name</Label>
                  <Input id="reg-name" value={regName} onChange={e => setRegName(e.target.value)} required placeholder="Your full name" className="glass-input rounded-xl h-11 text-sm" data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-white/60 text-sm font-medium">Email</Label>
                  <Input id="reg-email" type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} required placeholder="you@example.com" className="glass-input rounded-xl h-11 text-sm" data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-white/60 text-sm font-medium">Password</Label>
                  <Input id="reg-password" type="password" value={regPass} onChange={e => setRegPass(e.target.value)} required placeholder="Create a password" className="glass-input rounded-xl h-11 text-sm" data-testid="register-password-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-bio" className="text-white/60 text-sm font-medium">Bio <span className="text-white/25">(optional)</span></Label>
                  <Input id="reg-bio" value={regBio} onChange={e => setRegBio(e.target.value)} placeholder="Tell us about yourself and your audience" className="glass-input rounded-xl h-11 text-sm" data-testid="register-bio-input" />
                </div>
                <button type="submit" className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(247,148,29,0.3)] disabled:opacity-50" disabled={loading} data-testid="register-submit-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
                  {loading ? "Applying..." : "Apply as Ambassador"}
                </button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-xs text-white/20 mt-8">
          Eamico Inc. &middot; Write, ship, and scale.
        </p>
      </div>
    </div>
  );
}
